'use client';
import { useState } from 'react';
import {
  getTokensForMediaAsset,
  TypeTokenSetResponseData,
} from '@/lib/api/playbackService';

type VaultItemToken = {
  loading: boolean;
  error: string | undefined;
  retryCount: number;
  data: TypeTokenSetResponseData | undefined;
};

export type VaultItem = { playbackId: string; token: VaultItemToken };
export type RefetchTokenSetFn = (
  playbackId: string,
  forceRefetch: boolean,
) => void;
export type RetrieveTokenSetFn = (playbackId: string) => VaultItem | undefined;

type TokenSetVaultAccess = {
  retrieveItemFromVault: RetrieveTokenSetFn;
  fetchTokenSetForItem: RefetchTokenSetFn;
};

const MAX_RETRY_COUNT = 5;

export function formatTokens(vaultItem: VaultItem | undefined) {
  const tokens = vaultItem?.token.data?.token_set;

  // the mux player has token validation logic which will gracefully handle invalid token inputs.
  if (!tokens) {
    return undefined;
  }

  return {
    playback: tokens.video,
    thumbnail: tokens.thumbnail,
    storyboard: tokens.storyboard,
  };
}

export default function useTokenSetVault(
  playbackIds: string[] | undefined,
): TokenSetVaultAccess {
  const initialTokenState = {
    loading: true,
    error: undefined,
    data: undefined,
    retryCount: 0,
  } as VaultItemToken;

  const vaultItems =
    playbackIds &&
    playbackIds.map((playbackId) => ({
      playbackId,
      token: { ...initialTokenState },
    }));

  const emptyVault = [
    {
      playbackId: '',
      token: { ...initialTokenState },
    },
  ];

  const [vault, setVault] = useState<VaultItem[]>(vaultItems || emptyVault);

  function findAndUpdateVaultItemToken(
    playbackId: string,
    fields: Partial<VaultItemToken>,
  ) {
    setVault((prevState) => {
      const vaultItemIdx = prevState.findIndex(
        (vaultItem) => vaultItem.playbackId === playbackId,
      );

      const newState = [...prevState];
      if (vaultItemIdx !== -1) {
        newState[vaultItemIdx] = {
          ...newState[vaultItemIdx],
          token: {
            ...newState[vaultItemIdx].token,
            ...fields,
          },
        };
      }

      return newState;
    });
  }

  function setError(playbackId: string, error: string) {
    findAndUpdateVaultItemToken(playbackId, { loading: false, error });
  }

  function setOk(playbackId: string, data: TypeTokenSetResponseData) {
    findAndUpdateVaultItemToken(playbackId, { loading: false, data });
  }

  function setLoading(playbackId: string, retryCount: number) {
    findAndUpdateVaultItemToken(playbackId, { loading: true, retryCount });
  }

  function retrieveItemFromVault(playbackId: string): VaultItem | undefined {
    return vault.find((vaultItem) => vaultItem.playbackId === playbackId);
  }

  async function fetchTokenSetForItem(
    playbackId: string,
    forceRefresh = false,
  ) {
    const vaultItem = retrieveItemFromVault(playbackId);

    if (!vaultItem || vaultItem.token.retryCount === MAX_RETRY_COUNT) {
      return;
    }

    if (!forceRefresh && vaultItem?.token?.data) {
      return;
    }

    const retryCountIncremented = vaultItem.token.retryCount + 1;

    setLoading(playbackId, retryCountIncremented);
    getTokensForMediaAsset(playbackId)
      .then((data) => setOk(playbackId, data))
      .catch((errorMessage) => setError(playbackId, errorMessage));
  }

  return {
    retrieveItemFromVault,
    fetchTokenSetForItem,
  };
}
